/**
 * @file index.tsx
 *
 * @fileoverview Wildcard component that matches any route defined in drupal via
 * path alias.
 */

import React, { Component } from 'react';
import { User } from '~/jobiqo/models/user';
import { Router } from '~/jobiqo/components/Router';
import { ApolloClient, ApolloError } from '@apollo/client';
import initApollo, { addApolloState } from '~/jobiqo/apollo/initApollo';
import { GetServerSideProps } from 'next';
import { getLandingPageByPathQuery } from '~/jobiqo/pages/router/graphql/getLandingPageQueries';
import { commonQueries } from '~/jobiqo/lib/data/commonQueries';
import { isAnonymousByHeaders } from '~/jobiqo/lib/auth';
import { generateQueryStringURL } from '~/jobiqo/services/links';
import {
  getLandingPageOrganizationsVariables,
  GET_FEATURED_ORGANIZATIONS,
} from '~/jobiqo/components/Landingpage/Organizations/graphql';
import ErrorPage from '~/jobiqo/pages/error';
import { isRedirectCode, isPermanentRedirectCode } from '~/jobiqo/lib/redirect';
import { getServerFrontendConfig } from '~/jobiqo/lib/frontendConfig';
import {
  getAppConfig,
  getEnabledModules,
  getServerConfigByName,
} from '~/jobiqo/lib/config';

/**
 * Resolve the proper path to send to the landing page API.
 *
 * The backend needs some specific format and also we need to resolve properly the route
 * property from the Next router. WE should add a prefix when the language is not the default one
 * and no prefix when it is the default.
 *
 * @param ctx
 *   The current nextjs context object.
 *
 * @param fullPath
 *   If we want to get the full URL path and not remove the querystring.
 *
 * @returns {string}
 *  The Path to send to the API to resolve a landing page.
 */
const getFullPageUrlForLandingPage = (ctx, fullPath: boolean = false) => {
  // On the server we get an array in the "route" property of the query.
  // That is what we have defined in the route config because its a catch all route.
  // However in the client we just get the straight path as a string.
  const isServer = typeof window === 'undefined';
  let path = '';
  const { router, ...querystring } = ctx.query;

  let processedRouter = Array.isArray(router) ? router?.join('/') : router;
  const querystringUrl = generateQueryStringURL(querystring);
  if (querystringUrl) {
    processedRouter = `${processedRouter}?${querystringUrl}`;
  }

  if (isServer) {
    if (router?.length > 0) {
      path = `/${processedRouter}`;
    }
  } else {
    // Client url. On the client query.router might not exist. if it does
    // exist we use that otherwise we use the asPath property.
    if (router) {
      path = processedRouter;
    } else {
      path = ctx?.asPath.replace(ctx.locale, '');
    }
  }

  // To resolve landing pages the backend does not want use to send any querystring parameters as that would result
  // in a 404. So we remove it here.
  if (!fullPath) {
    path = path.indexOf('?') > 0 ? path.substring(0, path.indexOf('?')) : path;
  }

  return path;
};

interface RouterPageProps {
  path: string;
  /**
   * If the user is authenticated.
   */
  authenticated: boolean;
  /**
   * The user roles.
   */
  user: User;
  /**
   * The apollo client.
   */
  apolloClient: any;
  /**
   * The data for the queries ran on the server.
   */
  data: any;
  /**
   * If the data is loading.
   */
  loading: boolean;
  /**
   * If there was an error getting data.
   */
  error: ApolloError | undefined;
}

class RouterPage extends Component<RouterPageProps> {
  public render() {
    const { path, authenticated, user, data, loading, error } = this.props;

    if (error) {
      return <ErrorPage />;
    }

    return (
      <>
        <Router {...{ path, authenticated, user, data, error, loading }} />
      </>
    );
  }
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  try {
    const frontendConfig = getServerFrontendConfig(ctx);
    const backendConfig = await getAppConfig(ctx);
    const SITE_CAMPAIGN_ENABLED: boolean = !!getServerConfigByName(
      backendConfig,
      'site_campaign_enabled',
    )?.value[0];
    const landingPageQuery = getLandingPageByPathQuery(
      frontendConfig,
      getEnabledModules(backendConfig),
    );
    let path = getFullPageUrlForLandingPage(ctx);

    if (path === '') {
      // Drupal returns a redirect for '', we call with '/' to avoid the redirect.
      path = '/';
    }
    // If this is a not existing image or js path then we don't need to query
    // for a landing page and can return 404 directly. Examples:
    // /service-worker.js
    // /static/favicon-32x32.png
    // /static/safari-pinned-tab.svg
    if (/\.(jpe?g|png|gif|svg|webp|avif|ico|js)$/i.test(path)) {
      return {
        notFound: true,
      };
    }

    const apolloClient: ApolloClient<any> = initApollo({}, ctx);
    const isVisualEditor = ctx.query?.visual_editor === 'true';

    let { loading, error, data } = await apolloClient.query({
      query: landingPageQuery,
      variables: {
        path,
        language: ctx.locale,
      },
    });

    // In visual editor mode, if the route returns a redirect (e.g. /home),
    // re-query with the redirect target to get the actual content.
    if (
      isVisualEditor &&
      isRedirectCode(data?.route?.code) &&
      data?.route?.target
    ) {
      path = data.route.target;
      ({ loading, error, data } = await apolloClient.query({
        query: landingPageQuery,
        variables: {
          path,
          language: ctx.locale,
        },
      }));
    }

    if (path !== '/' && data.route.code === 404) {
      return {
        notFound: true,
      };
    }

    if (data?.route?.code === 403) {
      return {
        props: {},
        redirect: {
          destination: '/403',
        },
      };
    }

    // For checking redirects we do want to pass the full URL.
    let pathForRedirect = getFullPageUrlForLandingPage(ctx, true);
    if (pathForRedirect === '') {
      pathForRedirect = '/';
    }

    const { data: redirectData } = await apolloClient.query({
      query: landingPageQuery,
      variables: {
        path: pathForRedirect === '' ? '/' : pathForRedirect,
        language: ctx.locale,
      },
    });

    if (
      isRedirectCode(redirectData?.route?.code) &&
      path !== redirectData?.route?.target &&
      !isVisualEditor
    ) {
      // If we get a redirect response redirect to the target.
      return {
        redirect: {
          destination: redirectData.route.target,
          permanent: isPermanentRedirectCode(redirectData?.route?.code),
        },
        props: {},
      };
    }

    // Run all common queries like metatags and menus.
    await commonQueries(ctx, apolloClient, path, {
      siteCampaignEnabled: SITE_CAMPAIGN_ENABLED,
    });

    // Get organizations query.
    await apolloClient.query({
      query: GET_FEATURED_ORGANIZATIONS,
      variables: getLandingPageOrganizationsVariables(
        ctx,
        frontendConfig.ORGANIZATIONS,
      ),
    });

    if (error) {
      return {
        props: {
          error: true,
        },
      };
    }

    if (isAnonymousByHeaders(ctx.req)) {
      ctx.res.setHeader(
        'Cache-Control',
        'public, s-maxage=1800, stale-while-revalidate',
      );
    }

    return addApolloState(apolloClient, {
      props: {
        path,
        data,
        loading,
        error: error ?? false,
      },
    });
  } catch (e) {
    console.error('Failed to load route queries from the backend', e);
    return {
      props: {
        error: true,
      },
    };
  }
};

export default RouterPage;
